import { AxiosError } from "axios";
import { UNICO_ERROR_MESSAGES } from "./unico-create-process.constants";

/**
 * HTTP status codes that indicate Unico platform unavailability
 */
const UNAVAILABLE_STATUS_CODES = [400, 403, 404, 500];

/**
 * Checks if an error indicates Unico platform unavailability
 * @param error - Error object (AxiosError or generic Error)
 * @returns true if error indicates unavailability, false otherwise
 */
export const isUnicoUnavailableError = (
  error: unknown
): error is AxiosError => {
  if (!error) return false;

  // Check if it's an Axios error
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError;

    // Check HTTP status code
    if (axiosError.response?.status) {
      return UNAVAILABLE_STATUS_CODES.includes(axiosError.response.status);
    }

    // Check for network errors (no response)
    if (!axiosError.response) {
      return true; // Network errors should trigger retry
    }
  }

  // Check for generic network errors
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();
    if (
      errorMessage.includes("network") ||
      errorMessage.includes("timeout") ||
      errorMessage.includes("econnrefused")
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Gets appropriate error message based on HTTP status code
 * @param error - Error object
 * @returns Error message string
 */
export const getUnicoErrorMessage = (error: unknown): string => {
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosError = error as AxiosError;
    const status = axiosError.response?.status;

    if (status && status in UNICO_ERROR_MESSAGES) {
      return UNICO_ERROR_MESSAGES[status];
    }

    // Network error (no response)
    if (!axiosError.response) {
      return "Erro de conexão. Verifique sua internet e tente novamente.";
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Erro desconhecido ao acessar a plataforma Unico.";
};
